<template>
  <BottomSheet ref="bottomSheet" hashBase="withdrawal">
    <div class="relative w-full p-6">
      
      <!-- Close button -->
      <button @click="closeModal" class="absolute top-4 right-4 w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-500 rounded-full transition-colors z-10">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
      </button>

      <div class="text-2xl font-bold title-color mb-1 pr-8">
        Nouveau Retrait
      </div>
      <p class="text-xs text-gray-500 mb-5">
        Transférez vos gains vers votre compte Mobile Money
      </p>

      <!-- Step 0: Select amount and account -->
      <div v-if="step === 0" class="flex flex-col gap-4">
        <!-- Amount Input -->
        <div>
          <label class="block text-xs uppercase tracking-wider font-bold text-gray-500 mb-2">Montant à retirer (FCFA)</label>
          <input
            v-model.number="amount"
            type="number"
            :min="payoutLimits.min"
            :max="payoutLimits.max"
            :placeholder="`Min: ${payoutLimits.min}F`"
            class="w-full p-4 rounded-xl border border-gray-200 bg-white focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all font-bold tracking-wide text-lg"
            required
          />
        </div>

        <!-- Account Selection -->
        <div>
          <div class="flex items-center justify-between mb-2">
            <label class="block text-xs uppercase tracking-wider font-bold text-gray-500">
              Compte de destination
            </label>
            <button
              type="button"
              @click="openAddAccountModal"
              class="text-xs text-primary font-bold hover:underline"
            >
              + Ajouter un compte
            </button>
          </div>

          <DataFetcher dataKey="withdrawalAccounts" ref="accountsFetcher">
            <template #any="{ loading, data: accounts }">
              <div v-if="loading" class="space-y-2">
                <div class="shimmerContainer h-16 rounded-xl w-full"></div>
              </div>

              <div v-else-if="!accounts || accounts.length === 0" class="p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-xs">
                <p class="font-bold mb-1">Aucun compte de retrait enregistré</p>
                <p class="text-gray-600 mb-3">Veuillez d'abord ajouter et vérifier un compte Mobile Money pour effectuer un retrait.</p>
                <button
                  type="button"
                  @click="openAddAccountModal"
                  class="px-4 py-2 bg-primary text-white rounded-xl font-bold text-xs shadow-md"
                >
                  Ajouter un compte maintenant
                </button>
              </div>

              <div v-else class="space-y-2 max-h-48 overflow-y-auto pr-1">
                <div
                  v-for="acc in accounts"
                  :key="acc.id"
                  @click="selectedAccount = acc"
                  class="p-3.5 rounded-xl border cursor-pointer transition-all flex items-center justify-between"
                  :class="selectedAccount?.id === acc.id ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-gray-200 hover:border-gray-300 bg-white'"
                >
                  <div class="flex items-center gap-3">
                    <div
                      class="w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all"
                      :class="selectedAccount?.id === acc.id ? 'border-primary bg-primary' : 'border-gray-300'"
                    >
                      <div v-if="selectedAccount?.id === acc.id" class="w-2 h-2 rounded-full bg-white"></div>
                    </div>
                    <div>
                      <div class="font-bold text-sm title-color">
                        {{ acc.accountLabel }}
                      </div>
                      <div class="text-xs text-gray-500 font-mono">
                        {{ acc.accountNumber }} • <span class="uppercase font-bold text-gray-700">{{ acc.operator }}</span>
                      </div>
                    </div>
                  </div>

                  <span class="text-xs px-2.5 py-1 bg-green-50 text-green-700 rounded-lg font-semibold border border-green-200">
                    Vérifié ✓
                  </span>
                </div>
              </div>
            </template>
          </DataFetcher>
        </div>
      </div>

      <!-- Step 1: Loading -->
      <div v-else-if="step === 1" class="flex flex-col items-center justify-center py-10">
        <div class="w-16 h-16 relative flex items-center justify-center">
          <div class="absolute inset-0 rounded-full border-4 border-gray-100"></div>
          <div class="absolute inset-0 rounded-full border-4 border-primary border-t-transparent animate-spin"></div>
          <svg class="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
        </div>
        <div class="text-lg font-bold title-color mt-6 text-center">
          Traitement en cours...
        </div>
        <div class="text-sm text-gray-500 mt-2 text-center">Veuillez patienter quelques instants.</div>
      </div>

      <!-- Step 2: Result -->
      <div v-else-if="step === 2" class="flex flex-col gap-5 py-4">
        <div class="text-center">
          <div class="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg class="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"></path></svg>
          </div>
          <div class="text-2xl font-black text-gray-900 mb-2">
            Demande envoyée
          </div>
          <p class="text-gray-500 text-sm">{{ responseMessage }}</p>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="flex gap-3 mt-6" v-if="step === 0">
        <button
          @click="initiateWithdrawal"
          class="flex-1 py-4 px-6 rounded-2xl font-black text-white shadow-lg transition-all active:scale-95 disabled:opacity-50 disabled:grayscale"
          :class="isFormValid() ? 'bg-primary shadow-primary/30 hover:shadow-xl hover:shadow-primary/40' : 'bg-gray-400 shadow-none'"
          :disabled="!isFormValid()"
        >
          Retirer {{ formatPrice(amount || 0) }}
        </button>
      </div>

      <div class="flex gap-3 mt-6" v-if="step === 2">
        <button
          @click="closeModal"
          class="flex-1 py-4 px-6 rounded-2xl font-black border-2 border-primary text-primary hover:bg-primary/5 transition-all"
        >
          Fermer
        </button>
      </div>
    </div>
    
    <!-- Action component for trigger -->
    <ActionTrigger
      ref="withdrawalTrigger"
      dataKey="launchWithdrawal"
      @success="onWithdrawalConfirmed"
      @failed="onWithdrawalFailed"
    />

    <!-- Standalone FedaPay Pilot Verification Modal for Withdrawal Payout -->
    <AddFedapayWithdrawalAccount
      ref="fedapayVerification"
      @verified="onFedapayVerified"
      @cancelled="onFedapayCancelled"
      @error="onFedapayError"
    />
  </BottomSheet>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import Events from '@/events/events'
import { EVENTS } from '@/shared/constants/commons'
import ActionTrigger from '@/features/data/presentation/components/ActionTrigger.vue'
import DataFetcher from '@/features/data/presentation/components/DataFetcher.vue'
import BottomSheet from '@/presentation/components/dialog/BottomSheet.vue'
import AddFedapayWithdrawalAccount from '@/project/components/withdrawal/AddFedapayWithdrawalAccount.vue'
import { getProviderToUse, checkFedapayAccount, getPayoutLimit } from '@/project/data/services/withdrawal/withdrawalHelpers'
import Storage from '@/shared/utils/storage'

const withdrawalTrigger = ref(null)
const accountsFetcher = ref(null)
const bottomSheet = ref(null)
const fedapayVerification = ref(null)

const step = ref(0)
const amount = ref(null)
const selectedAccount = ref(null)
const responseMessage = ref('')
const payoutLimits = ref({ min: 1000, max: 10000 })

let openModalHandler = null
let accountCreatedHandler = null

onMounted(() => {
  openModalHandler = Events.handle('open_withdrawal_modal', (targetAccount) => {
    openModal(targetAccount)
  })

  accountCreatedHandler = Events.handle('withdrawal_account_created', () => {
    if (accountsFetcher.value?.refresh) {
      accountsFetcher.value.refresh()
    }
  })
})

onUnmounted(() => {
  if (openModalHandler) Events.removeHandler(openModalHandler)
  if (accountCreatedHandler) Events.removeHandler(accountCreatedHandler)
})

function formatPrice(price) {
  if (!price) return '0 FCFA'
  return new Intl.NumberFormat('fr-BJ', {
    style: 'currency',
    currency: 'XOF',
    maximumFractionDigits: 0
  }).format(price)
}

async function openModal(targetAccount = null) {
  step.value = 0
  amount.value = null
  responseMessage.value = ''
  
  if (targetAccount && targetAccount.id) {
    selectedAccount.value = targetAccount
  } else {
    selectedAccount.value = null
  }

  try {
    const limits = await getPayoutLimit('rider')
    if (limits) {
      payoutLimits.value = limits
    }
  } catch (e) {
    console.error('Erreur chargement limites de retrait rider:', e)
  }

  if (accountsFetcher.value?.refresh) {
    accountsFetcher.value.refresh()
  }

  bottomSheet.value?.show()
}

function closeModal() {
  bottomSheet.value?.hide()
}

function openAddAccountModal() {
  closeModal()
  Events.dispatch('open_add_withdrawal_account')
}

function isFormValid() {
  return (
    amount.value >= payoutLimits.value.min &&
    (!payoutLimits.value.max || amount.value <= payoutLimits.value.max) &&
    selectedAccount.value &&
    selectedAccount.value.accountNumber
  )
}

async function initiateWithdrawal() {
  if (!isFormValid()) return

  const provider = await getProviderToUse(false)
  const isFeda = (provider || '').toLowerCase() === 'fedapay-pilot'

  const cleanPhone = String(selectedAccount.value.accountNumber).replace(/\s/g, '')
  const rawPhone = cleanPhone.startsWith('229') && cleanPhone.length > 8 ? cleanPhone.slice(3) : cleanPhone

  if (isFeda) {
    const checkRes = await checkFedapayAccount(rawPhone, selectedAccount.value.operator)
    if (!checkRes.exists || !checkRes.isVerified) {
      Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
        type: 'info',
        title: 'Validation requise',
        body: 'Votre compte doit être validé avec un débit de confirmation de 1 FCFA avant de pouvoir effectuer le retrait.',
      })

      const accountData = {
        owner: selectedAccount.value.accountLabel || selectedAccount.value.owner,
        accountOwnerName: selectedAccount.value.accountLabel || selectedAccount.value.owner,
        rawPhoneNumber: rawPhone,
        phoneNumber: cleanPhone,
        operator: selectedAccount.value.operator || 'MTN',
        country: selectedAccount.value.country || 'Bénin',
      }

      if (fedapayVerification.value) {
        fedapayVerification.value.startVerification(accountData)
      }
      return
    }
  }

  executeWithdrawal()
}

function executeWithdrawal() {
  step.value = 1
  
  const userData = Storage.get('userData') || {}
  
  const payload = {
    amount: amount.value,
    phoneNumber: String(selectedAccount.value.accountNumber).replace(/\s/g, ''),
    firstName: selectedAccount.value.accountLabel || userData.firstName || '',
    lastName: userData.lastName || '',
    operator: selectedAccount.value.operator,
  }
  
  if (withdrawalTrigger.value) {
    withdrawalTrigger.value.trigger(payload)
  }
}

function onFedapayVerified() {
  executeWithdrawal()
}

function onFedapayCancelled() {
  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
    type: 'info',
    title: 'Annulé',
    body: 'Opération de retrait interrompue.',
  })
}

function onFedapayError(err) {
  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
    type: 'error',
    title: 'Erreur de vérification',
    body: err?.message || 'Échec de la validation du compte sur Mobile Money.',
  })
}

function onWithdrawalConfirmed(res) {
  step.value = 2
  responseMessage.value = res?.message || 'Demande de retrait enregistrée. Vous serez crédité prochainement.'
  Events.dispatch('withdrawal_launched')
}

function onWithdrawalFailed(err) {
  step.value = 0
  
  const rawError = err?.result?.data?.raw?.error || err?.result?.raw?.error || err?.error?.metadata?.res?.raw?.error
  const isDisplayable = err?.result?.isDisplayable || rawError?.isDisplayable || (rawError?.isOperational && rawError?.isDisplayable)
  
  const message = (isDisplayable && (err?.result?.message || rawError?.message)) 
    || 'Erreur lors de la demande de retrait.'

  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
    type: 'error',
    title: 'Demande échouée',
    body: message,
  })
}
</script>

<script>
export default {
  name: 'WithdrawalModal'
}
</script>
